import { initializeApp } from "firebase/app";
import {
  User,
  confirmPasswordReset,
  getAuth,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  verifyPasswordResetCode,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);

// Nastavení persistence s fallbackem (důležité pro mobilní prohlížeče)
(async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    // console.debug('Auth persistence: local');
  } catch (_e) {
    try {
      await setPersistence(auth, browserSessionPersistence);
      // console.warn('Auth persistence fallback: session');
    } catch (_e2) {
      // console.error('Auth persistence could not be set', _e2);
    }
  }
})();

export enum FirebaseResponseCode {
  OK,
  BAD_REQUEST,
  INTERNAL_ERROR,

  INVALID_LINK
}

export class FirebaseResponse {
  code: FirebaseResponseCode;
  message: string;

  constructor(
    code: FirebaseResponseCode,
    message: string,
  ) {
    this.code = code;
    this.message = message;
  }
}

export class FirebaseLoginResponse extends FirebaseResponse {
  user: User | null;

  constructor(
    code: FirebaseResponseCode,
    message: string,
    user: User | null = null
  ) {
    super(code, message)
    this.user = user;
  }
}


export const getCurrentUserToken = async (): Promise<string | null> => {
  return new Promise((resolve, reject) => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          resolve(token);
        } catch (error) {
          console.error("Error fetching ID token:", error);
          reject(error);
        }
      } else {
        console.log("No user is currently signed in.");
        resolve(null);
      }
    });
  });
};

export async function loginWithEmail(
  email: string,
  password: string
): Promise<FirebaseLoginResponse> {
  try {
    let userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );

    return new FirebaseLoginResponse(
      FirebaseResponseCode.OK,
      "Sign in successful",
      userCredential.user
    );
  } catch (error) {
    return handleFirebaseLoginError(error);
  }
}

export async function finishSignUp(email: string): Promise<boolean> {
  if (isSignInWithEmailLink(auth, window.location.href) === false) {
    return false;
  }

  try {
    await signInWithEmailLink(auth, email, window.location.href);
  } catch (error) {
    console.error(error);
    return false;
  }

  return true;
}

export async function confirmNewPassword(oobCode: string, newPassword: string): Promise<FirebaseResponse> {
  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
  } catch (error: any) {
    if (error.code === 'auth/expired-action-code') {
      return new FirebaseResponse(FirebaseResponseCode.INVALID_LINK, "The link has expired. Please request a new link.");
    } else if (error.code === 'auth/invalid-action-code') {
      return new FirebaseResponse(FirebaseResponseCode.INVALID_LINK, "The link is invalid. Please request a new link.");
    }
    else {
      return new FirebaseResponse(FirebaseResponseCode.INTERNAL_ERROR, `Error resetting password: ${error.message}`);
    } 
  }

  return new FirebaseResponse(FirebaseResponseCode.OK, "New password confirmed");
}

export async function validatePasswordResetLink(oobCode: string): Promise<FirebaseResponse> {
  try {

    await verifyPasswordResetCode(auth, oobCode);
  } catch (error: any) {
    if (error.code === 'auth/expired-action-code') {
      return new FirebaseResponse(FirebaseResponseCode.INVALID_LINK, "The link has expired. Please request a new link.");
    } else if (error.code === 'auth/invalid-action-code') {
      return new FirebaseResponse(FirebaseResponseCode.INVALID_LINK, "The link is invalid. Please request a new link.");
    }
    else {
      return new FirebaseResponse(FirebaseResponseCode.INTERNAL_ERROR, `Error validating the link: ${error.message}`);
    } 
  }

  return new FirebaseResponse(FirebaseResponseCode.OK, "Link is valid");
}

export async function sendPasswordReset(email: string): Promise<FirebaseResponse> {
  try {
    await sendPasswordResetEmail(auth, email);
    return new FirebaseResponse(FirebaseResponseCode.OK, "Password reset email sent successfully");
  }
  catch (error: any) {
    console.error("Error sending password reset email:", error);
    
    // Handle specific Firebase auth errors
    if (error.code === 'auth/user-not-found') {
      return new FirebaseResponse(FirebaseResponseCode.BAD_REQUEST, "No account found with this email address");
    } else if (error.code === 'auth/invalid-email') {
      return new FirebaseResponse(FirebaseResponseCode.BAD_REQUEST, "Invalid email address");
    } else if (error.code === 'auth/too-many-requests') {
      return new FirebaseResponse(FirebaseResponseCode.BAD_REQUEST, "Too many requests. Please try again later");
    }
    
    return new FirebaseResponse(FirebaseResponseCode.INTERNAL_ERROR, `Error sending password reset email: ${error.message}`);
  }
}

export async function resentPasswordResetLink(email: string): Promise<FirebaseResponse> {
  // Use the simpler password reset function
  return sendPasswordReset(email);
}

function handleFirebaseLoginError(error: any): FirebaseLoginResponse {
  console.error(error);

  switch (error.code) {
    case "auth/user-disabled":
      return new FirebaseLoginResponse(
        FirebaseResponseCode.BAD_REQUEST,
        "That account has been disabled."
      );
    case "auth/user-not-found":
    case "auth/wrong-password":
      return new FirebaseLoginResponse(
        FirebaseResponseCode.BAD_REQUEST,
        "The email or password is incorrect."
      );
    case "auth/invalid-email":
      return new FirebaseLoginResponse(
        FirebaseResponseCode.BAD_REQUEST,
        "That email isn't valid. Please try a valid email address."
      );
    case "auth/invalid-login-credentials":
      return new FirebaseLoginResponse(
        FirebaseResponseCode.BAD_REQUEST,
        "Invalid credentials."
      );
    case "auth/missing-password":
      return new FirebaseLoginResponse(
        FirebaseResponseCode.BAD_REQUEST,
        "Missing password."
      );
    default:
      return new FirebaseLoginResponse(
        FirebaseResponseCode.INTERNAL_ERROR,
        "Something went wrong. Please try logging in again."
      );
  }
}
